import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { safeExperienceDestination } from '@/lib/member-experience';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
} from '@/hooks/useNotifications';
import type { AppNotification } from '@/services/notifications.service';
import {
  Bell,
  Info,
  CheckCheck,
  Loader2,
  BookOpen,
  CreditCard,
  BellOff,
  Megaphone,
  ExternalLink,
  MessageCircle,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { AppToolTip } from './AppToolTip';

function getNotificationIcon(category?: string) {
  switch (category) {
    case 'announcement': return Megaphone;
    case 'comment': return MessageCircle;
    case 'community': return Users;
    case 'course': return BookOpen;
    case 'payment': return CreditCard;
    default: return Info;
  }
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function NotificationItem({
  notification,
  onRead,
}: {
  notification: AppNotification;
  onRead: (n: AppNotification) => void;
}) {
  const Icon = getNotificationIcon(notification.category);

  return (
    <button
      type="button"
      onClick={() => onRead(notification)}
      className={cn(
        'group relative flex w-full items-start gap-3 rounded-md px-4 py-3 text-left transition-colors duration-150',
        'hover:bg-secondary hover:text-secondary-foreground active:bg-secondary/80',
        !notification.read && 'bg-primary/[0.03]',
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
          notification.read ? 'bg-muted' : 'bg-primary/10',
        )}
      >
        <Icon
          className={cn(
            'h-4 w-4',
            notification.read ? 'text-muted-foreground' : 'text-primary',
          )}
        />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              'min-w-0 break-words text-[13px] leading-snug',
              notification.read
                ? 'text-muted-foreground'
                : 'text-foreground font-medium',
            )}
          >
            {notification.title}
          </p>
          <span className="text-muted-foreground mt-0.5 shrink-0 text-[10px]">
            {timeAgo(notification.createdAt)}
          </span>
        </div>
        <p className="text-muted-foreground mt-0.5 line-clamp-2 break-words text-xs leading-relaxed">
          {notification.message}
        </p>

        {/* Clickable hint */}
        {notification.actionUrl && !notification.read && (
          <p className="text-primary mt-1 text-[10px] font-medium opacity-0 transition-opacity group-hover:opacity-100">
            Clique para ver &rarr;
          </p>
        )}
      </div>
    </button>
  );
}

type FilterTab = 'all' | 'unread';

export function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<FilterTab>('all');
  const [selectedNotification, setSelectedNotification] = useState<AppNotification | null>(null);
  const { user } = useAuthStore();
  const selected = selectedNotification?.userId === user?.id ? selectedNotification : null;
  const destination = safeExperienceDestination(selected?.actionUrl);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: notificationsData, isLoading, isError, refetch } = useNotifications(1, 50);
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  // Refetch when dropdown opens
  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  }, [open, queryClient]);

  const notifications = notificationsData?.data || [];

  const filtered =
    filter === 'unread'
      ? notifications.filter((n: AppNotification) => !n.read)
      : notifications;

  const handleRead = useCallback(
    (notification: AppNotification) => {
      if (!notification.read) {
        markAsRead.mutate(notification.id);
      }

      if (notification.category === 'announcement') {
        setSelectedNotification(notification);
        setOpen(false);
        return;
      }

      // Preserve legacy lesson destinations while keeping external URLs safe.
      const actionUrl = safeExperienceDestination(notification.actionUrl);
      if (actionUrl?.startsWith('https://')) {
        setSelectedNotification(notification);
        setOpen(false);
      } else if (actionUrl) {
        // Fix legacy URL patterns
        let url = actionUrl;
        if (url.startsWith('/courses/lesson/')) {
          url = url.replace('/courses/lesson/', '/lesson/');
        }
        navigate(url);
        setOpen(false);
      } else if (notification.category === 'community') {
        navigate('/community');
        setOpen(false);
      } else if (notification.category === 'comment') {
        const meta = notification.metadata as Record<string, unknown>;
        if (meta?.lessonId) {
          navigate(`/lesson/${meta.lessonId}`);
          setOpen(false);
        }
      }
    },
    [markAsRead, navigate],
  );

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <AppToolTip content="Notificações">
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            aria-label={
              unreadCount > 0
                ? `Notificações, ${unreadCount} não lidas`
                : 'Notificações'
            }
            variant="ghost"
            size="icon"
            className="relative"
          >
            <Bell className="text-muted-foreground h-[18px] w-[18px]" aria-hidden="true" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                {unreadCount > 99 ? '99' : unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
      </AppToolTip>
      <PopoverContent
        aria-label="Notificações"
        align="end"
        className="border-border/60 bg-card w-[calc(100vw-2rem)] overflow-hidden rounded-xl border p-0 shadow-2xl sm:w-96"
        sideOffset={8}
        onCloseAutoFocus={event => { if (selected) event.preventDefault(); }}
      >
        {/* Header */}
        <div className="border-border/40 border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Notificações</h3>
              {unreadCount > 0 && (
                <span className="bg-primary/15 text-primary rounded-full px-2 py-0.5 text-[10px] font-semibold">
                  {unreadCount} {unreadCount === 1 ? 'nova' : 'novas'}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <Button
                aria-label="Marcar todas como lidas"
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                disabled={markAllAsRead.isPending}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Marcar lidas</span>
              </Button>
            )}
          </div>

          {/* Filter tabs */}
          <div className="mt-2.5 flex gap-1">
            {(['all', 'unread'] as FilterTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                aria-pressed={filter === tab}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-medium transition-colors duration-150',
                  filter === tab
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {tab === 'all' ? 'Todas' : 'Não lidas'}
              </button>
            ))}
          </div>
        </div>

        {(markAsRead.isError || markAllAsRead.isError) && (
          <p role="alert" className="px-4 py-3 text-sm text-destructive">Não foi possível marcar como lida. Tente novamente.</p>
        )}
        {/* Scrollable list */}
        <div
          ref={scrollRef}
          className="max-h-[70vh] overflow-y-auto overscroll-contain sm:max-h-[480px]"
          style={{ scrollbarWidth: 'thin' }}
        >
          {isError ? (
            <div role="alert" className="space-y-3 px-5 py-8 text-sm">
              <p>Não foi possível carregar as notificações.</p>
              <Button variant="outline" size="sm" onClick={() => { void refetch(); }}>Tentar novamente</Button>
            </div>
          ) : isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="text-primary h-6 w-6 animate-spin" />
              <p className="text-muted-foreground mt-2 text-xs">
                Carregando...
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="bg-muted/50 flex h-14 w-14 items-center justify-center rounded-full">
                {filter === 'unread' ? (
                  <CheckCheck className="text-primary h-7 w-7" />
                ) : (
                  <BellOff className="text-muted-foreground h-7 w-7" />
                )}
              </div>
              <p className="text-foreground mt-3 text-sm font-medium">
                {filter === 'unread' ? 'Tudo em dia!' : 'Nenhuma notificação'}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                {filter === 'unread'
                  ? 'Você não tem notificações pendentes'
                  : 'Suas notificações aparecerão aqui'}
              </p>
            </div>
          ) : (
            <div className="divide-border/30 divide-y">
              {filtered.map((notification: AppNotification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={handleRead}
                />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
    <Dialog open={!!selected} onOpenChange={next => { if (!next) setSelectedNotification(null); }}>
      <DialogContent onCloseAutoFocus={event => { event.preventDefault(); triggerRef.current?.focus(); }}>
        <DialogHeader className="pr-6 text-left">
          <DialogTitle className="break-words">{selected?.title}</DialogTitle>
          <DialogDescription>
            {selected && new Date(selected.createdAt).toLocaleString('pt-BR', { dateStyle: 'long', timeStyle: 'short' })}
          </DialogDescription>
        </DialogHeader>
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{selected?.message}</p>
        {markAsRead.isError && <p role="alert" className="text-sm text-destructive">Não foi possível marcar como lida. Você pode tentar novamente pelo sino.</p>}
        {destination && (
          <Button asChild className="justify-self-start" onClick={() => setSelectedNotification(null)}>
            {destination.startsWith('https://') ? (
              <a href={destination} target="_blank" rel="noopener noreferrer">Abrir destino <ExternalLink className="h-4 w-4" aria-hidden="true" /></a>
            ) : (
              <Link to={destination}>Abrir destino</Link>
            )}
          </Button>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
